import type { ChoiceOption, MessageChoices } from '../../../../lib/message-choices';
import { useTranslation } from '../../../../lib/i18n';

// Choice chips — a structured ask's options as pill buttons under the assistant
// body. Selection derives from the transcript (a USER row carrying a matching
// choiceAnswer), so an answered chip gets the accent treatment — same visual
// language as the caller's own reaction chip — but every chip stays tappable:
// another tap is another turn, and free text is always valid. No handler
// (read-only surface) renders the pills inert.
export function ChoiceChips({
  choices,
  selectedIds,
  onSelect,
}: {
  choices: MessageChoices;
  selectedIds: ReadonlySet<string>;
  onSelect?: (option: ChoiceOption) => void;
}) {
  const { t } = useTranslation();
  if (choices.options.length === 0) return null;
  return (
    <div role="group" aria-label={t('chat.choices.groupLabel')} style={CHIPS_ROW}>
      {choices.options.map((option) => {
        const selected = selectedIds.has(option.id);
        return (
          <button
            key={option.id}
            type="button"
            disabled={!onSelect}
            onClick={onSelect ? () => onSelect(option) : undefined}
            aria-pressed={selected}
            dir="auto"
            style={{
              ...PILL,
              ...(selected ? PILL_SELECTED : {}),
              ...(onSelect ? {} : { cursor: 'default' }),
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

const CHIPS_ROW: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 6,
  marginTop: 8,
  maxWidth: '100%',
};

const PILL: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '5px 14px',
  borderRadius: 999,
  border: '1px solid rgba(var(--hairline-rgb), 0.28)',
  background: 'rgba(var(--ink-rgb), 0.04)',
  color: 'rgba(var(--ink-rgb), 0.85)',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '18px',
  maxWidth: '100%',
  textAlign: 'start',
};

// The accent treatment the caller's own reaction chip uses (CHIP_MINE).
const PILL_SELECTED: React.CSSProperties = {
  border: '1px solid rgba(var(--accent-rgb), 0.75)',
  background: 'rgba(var(--accent-rgb), 0.16)',
};
